import { useRef } from 'react';
import { Box, Container, Typography } from '@mui/material';
import { landingColors, landingFont, eyebrowSx, sectionHeadingSx } from '../landingTheme';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { getLandingIcon } from '../landingIcons';
import { PLATFORM_FEATURES } from '../landingContent';

export default function FeaturesSection() {
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef);

  return (
    <Box
      ref={sectionRef}
      component="section"
      id="features-section"
      aria-label="Platform Features"
      sx={{ py: { xs: 8, md: 14 }, backgroundColor: landingColors.night, color: '#fff' }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2.5, md: 4 } }}>
        <Box sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto', mb: { xs: 5, md: 8 } }}>
          <Typography data-reveal sx={{ ...eyebrowSx, color: landingColors.indigo, fontFamily: landingFont, mb: 1.5 }}>
            Platform Features
          </Typography>
          <Typography data-reveal data-reveal-delay="0.06" component="h2" sx={{ ...sectionHeadingSx, fontFamily: landingFont, mb: 2 }}>
            Everything your restaurant needs
          </Typography>
          <Typography data-reveal data-reveal-delay="0.12" sx={{ fontFamily: landingFont, color: landingColors.onNightMuted, fontSize: { xs: '1rem', md: '1.2rem' }, lineHeight: 1.6 }}>
            From building your digital menu to processing payments and managing your team, one complete platform.
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: { xs: 2.5, md: 3 } }}>
          {PLATFORM_FEATURES.map((feature, index) => {
            const Icon = getLandingIcon(feature.icon);
            return (
              <Box
                key={feature.title}
                data-reveal
                data-reveal-delay={((index % 3) * 0.08).toString()}
                sx={{
                  p: { xs: 3, md: 3.5 },
                  borderRadius: '20px',
                  backgroundColor: landingColors.nightPanel,
                  border: `1px solid ${landingColors.nightLine}`,
                  height: '100%',
                  transition: 'transform 0.3s ease, border-color 0.3s ease',
                  '&:hover': { transform: 'translateY(-6px)', borderColor: landingColors.indigo },
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '14px',
                    backgroundColor: 'rgba(102,126,234,0.16)',
                    color: landingColors.indigo,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2.5,
                  }}
                >
                  <Icon sx={{ fontSize: 28 }} />
                </Box>
                <Typography component="h3" sx={{ fontFamily: landingFont, fontWeight: 700, fontSize: '1.2rem', mb: 1 }}>
                  {feature.title}
                </Typography>
                <Typography sx={{ fontFamily: landingFont, color: landingColors.onNightMuted, fontSize: '0.95rem', lineHeight: 1.65 }}>
                  {feature.description}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
}
